/**
 * Price sparkline with dashed guides, y-axis labels and the date range — the
 * chart every coin page uses. Shared so INCPT renders through the same
 * component as the on-chain coins rather than a lookalike.
 */
export function PriceChart({ points }: { points: [number, number][] }) {
  const W = 320;
  const H = 120;
  // Keep stroke (2px) inside the viewBox — max/min used to sit on y=0 / y=H and clipped.
  const PAD = 6;
  if (points.length < 2) return <div className="h-[120px]" />;

  const prices = points.map((p) => p[1]);
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const range = max - min || 1;
  const up = prices[prices.length - 1]! >= prices[0]!;
  const color = up ? '#69cc8b' : '#ff5a5a';

  const xy = points.map((p, i) => {
    const x = (i / (points.length - 1)) * W;
    const y = H - PAD - ((p[1] - min) / range) * (H - 2 * PAD);
    return [x, y] as const;
  });
  const line = xy.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ');
  const area = `${line} L${W} ${H} L0 ${H} Z`;

  const dec = max < 10 ? 4 : 2;
  const yLabels = [max, max - range / 3, min + range / 3, min].map((v) => v.toFixed(dec));
  const fmtT = (ts: number, withYear = false) =>
    new Date(ts).toLocaleDateString(
      undefined,
      withYear
        ? { day: 'numeric', month: 'short', year: 'numeric' }
        : { day: 'numeric', month: 'short' },
    );

  return (
    <div className="flex flex-col gap-3">
      {/* Axis labels on the left, as on the prototype's token page. */}
      <div className="flex gap-3">
        <div className="flex w-12 flex-col justify-between text-right text-[11px] tabular-nums text-black/[0.44]">
          {yLabels.map((l, i) => (
            <span key={i}>{l}</span>
          ))}
        </div>
        <div className="relative h-[120px] flex-1">
          <div className="absolute inset-0 flex flex-col justify-between">
            {yLabels.map((_, i) => (
              <div key={i} className="border-t border-dashed border-black/[0.1]" />
            ))}
          </div>
          <svg
            viewBox={`0 0 ${W} ${H}`}
            preserveAspectRatio="none"
            className="absolute inset-0 h-full w-full"
          >
            <defs>
              <linearGradient id="coinArea" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity="0.28" />
                <stop offset="100%" stopColor={color} stopOpacity="0" />
              </linearGradient>
            </defs>
            <path d={area} fill="url(#coinArea)" />
            <path
              d={line}
              fill="none"
              stroke={color}
              strokeWidth="2"
              strokeLinejoin="round"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
          </svg>
        </div>
      </div>
      <div className="flex">
        <div className="w-12" />
        <div className="flex flex-1 justify-between text-[12px] text-black/[0.44]">
          <span>{fmtT(points[0]![0], true)}</span>
          <span>{fmtT(points[points.length - 1]![0])}</span>
        </div>
      </div>
    </div>
  );
}
